import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Package, Award, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IMAGES } from '@/assets/images';

// Primary Aluminum alloy tables from mechem.com
const standardPurityTable = [
  { alloy: 'P0406', si: '0.04', fe: '0.06', zn: '0.03', v: '0.02', ga: '0.03', al: '99.85', othersEach: '0.02', othersTotal: '0.05' },
  { alloy: 'P0610', si: '0.06', fe: '0.10', zn: '0.015', v: '0.010', ga: '0.009', al: '99.80', othersEach: '0.02', othersTotal: '0.05' },
  { alloy: 'P1020', si: '0.10', fe: '0.20', zn: '0.03', v: '0.03', ga: '0.04', al: '99.70', othersEach: '0.03', othersTotal: '0.10' },
];

const highPurityTable = [
  { alloy: 'P0101', si: '0.0150', fe: '0.0120', ti: '0.0050', zn: '0.0020', cr: '0.0010', na: '0.0020', v: '0.0024', b: '0.0020', ga: '0.0070', ca: '0.0020', ni: '0.0020', li: '0.0005', al: '99.95', othersEach: '0.0020', othersTotal: '0.0100' },
  { alloy: 'P0202', si: '0.020', fe: '0.020', ti: '0.020', zn: '0.010', cr: '0.020', na: '-', v: '-', b: '-', ga: '-', ca: '-', ni: '-', li: '-', al: '99.90', othersEach: '0.010', othersTotal: '0.020' },
  { alloy: 'P0303', si: '0.030', fe: '0.030', ti: '0.003', zn: '0.01', cr: '0.03', na: '-', v: '-', b: '-', ga: '-', ca: '-', ni: '-', li: '-', al: '99.85', othersEach: '0.010', othersTotal: '0.020' },
  { alloy: 'P0404', si: '0.040', fe: '0.040', ti: '0.003', zn: '0.0100', cr: '0.01', na: '0.03', v: '0.0100', b: '-', ga: '-', ca: '-', ni: '-', li: '-', al: '99.85', othersEach: '0.01', othersTotal: '0.03' },
];

const aSeriesTable = [
  { alloy: 'A6', si: '0.18', fe: '0.25', mn: '0.03', mg: '0.03', cu: '0.01', ti: '0.02', zn: '0.05', sn: '0.03', ga: '99.60', pb: '-', al: '-' },
  { alloy: 'A5', si: '0.25', fe: '0.30', mn: '0.05', mg: '0.03', cu: '0.02', ti: '0.02', zn: '0.06', sn: '0.03', ga: '99.50', pb: '-', al: '-' },
  { alloy: 'A35', si: '0.25', fe: '0.40', mn: '0.05', mg: '0.05', cu: '0.05', ti: '0.02', zn: '0.1', sn: '-', ga: '99.35', pb: '-', al: '-' },
  { alloy: 'A0', si: '0.25', fe: '0.50', mn: '0.05', mg: '0.05', cu: '0.03', ti: '0.02', zn: '0.10', sn: '-', ga: '99.00', pb: '-', al: '-' },
  { alloy: 'AV97', si: '1.00', fe: '2.50', mn: '0.1', mg: '0.1', cu: '0.1', ti: '0.1', zn: '0.1', sn: '-', ga: '97.00', pb: '-', al: '-' },
];

const ecGradeTable = [
  { alloy: 'A7 EC Grade', si: '0.08', fe: '0.20', mn: '0.01', mg: '0.01', cu: '0.01', ti: '0.003', zn: '0.03', cr: '0.003', v: '0.003', b: '0.02', ga: '0.02', pb: '0.01', ni: '0.01', al: '99.70' },
];

// Primary Alloyed Aluminium tables from mechem.com
const alloyedBilletTable = [
  { alloy: '6060HP', si: '0.40–0.44', fe: '0.15–0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.36–0.40', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6060-30', si: '0.40–0.45', fe: '0.15–0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.40–0.45', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6060-30s', si: '0.42-0.47', fe: '0.16-0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.39-0.45', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6060-90', si: '0.50-0.55', fe: '0.16-0.22', cu: '0.02', mn: '0.02-0.03', mg: '0.50-0.55', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6063-35I', si: '0.42–0.46', fe: '0.15–0.20', cu: '0.02', mn: '0.01-0.03', mg: '0.42–0.47', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6063-35S', si: '0.42-0.46', fe: '0.14-0.19', cu: '0.02', mn: '0.01-0.03', mg: '0.46-0.50', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6005', si: '0.58-0.63', fe: '0.18-0.22', cu: '0.10-0.2', mn: '0.13-0.17', mg: '0.53-0.58', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '-', othersTotal: '0.10' },
  { alloy: '6061', si: '0.61-0.66', fe: '0.14-0.20', cu: '0.20-0.30', mn: 'max 0.05', mg: '0.90-0.96', zn: '0.02', cr: '0.06-0.19', ti: '0.02-0.05', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: '6082', si: '0.95-1.05', fe: '0.15-0.23', cu: 'max 0.03', mn: '0.50-0.57', mg: '0.62-0.67', zn: '0.02', cr: '0.02', ti: '0.02', othersEach: '-', othersTotal: '0.10' },
  { alloy: '6463', si: '0.34-0.38', fe: '0.06-0.13', cu: '0.10-0.15', mn: '0.03-0.05', mg: '0.45-0.53', zn: '0.02', cr: '0.03', ti: '0.05', othersEach: '0.02', othersTotal: '0.10' },
  { alloy: 'P1020', si: 'Max 0.08', fe: 'Max 0.20', cu: 'Max 0.01', mn: 'Max 0.01', mg: 'Max 0.02', zn: '0.04', cr: 'Max 0.01', ti: 'Max 0.01', othersEach: '0.02', othersTotal: '0.10' },
];

const slabTable = [
  { thickness: '600', width: '1630', length: '3800' },
  { thickness: '600', width: '1630', length: '3020' },
  { thickness: '600', width: '1630', length: '4115' },
  { thickness: '600', width: '1750', length: '3810' },
  { thickness: '560', width: '1310', length: '4500' },
  { thickness: '560', width: '1100', length: '4000' },
];

const castingIngotsTable = [
  { alloy: 'AlSi10Mg', si: '9.00-11.00', fe: '1.00', mn: '0.001-0.4', mg: '0.2-0.5', cu: '0.1', ti: '0.15', zn: '0.1', cr: '0.05', sn: '-', sr: '-', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi7MgSr', si: '6.60-7.30', fe: '0.13', mn: '0.05', mg: '0.25-0.33', cu: '0.02', ti: '0.10-0.15', zn: '0.05', cr: '0.005', sn: '0.01', sr: '0.013-0.023', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi11MgSr', si: '10.70-11.60', fe: '0.1300', mn: '0.04', mg: '0.12-0.17', cu: '0.02', ti: '0.10-0.15', zn: '0.04', cr: '0.005', sn: '0.01', sr: '0.0350-0.0450', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi10MgMn', si: '9.50-11.50', fe: '0.15', mn: '0.60-0.80', mg: '0.15-0.30', cu: '0.03', ti: '0.04-0.15', zn: '0.07', cr: '0.03', sn: '0.005', sr: '0.015-0.030', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: '145-piston alloy', si: '11.00-12.00', fe: '0.20-0.30', mn: '0.15', mg: '0.8-1.40', cu: '0.80-1.5', ti: '0.08', zn: '0.02', cr: '0.05', sn: '-', sr: '-', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: '175-piston alloy', si: '11.00-12.00', fe: '0.20-0.30', mn: '0.15', mg: '0.80-1.40', cu: '3.00-4.50', ti: '0.05', zn: '0.08', cr: '0.05', sn: '2.00-3.00', sr: '0.001-0.004', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi9Cu3', si: '8.00-9.50', fe: '0.70-1.00', mn: '0.40-0.65', mg: '0.15-0.40', cu: '3.00-3.50', ti: '0.15', zn: '1.00', cr: '0.10', sn: '0.0050', sr: '-', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi50', si: '47-53', fe: '0.30', mn: '0.05', mg: 'Max 0.05', cu: '0.05', ti: '0.05', zn: '0.05', cr: '0.05', sn: '0.15', sr: '-', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
  { alloy: 'AlSi20', si: '18-22', fe: '0.45', mn: '0.10', mg: '0.10', cu: '0.10', ti: '0.10', zn: '0.10', cr: '-', sn: '-', sr: '-', al: 'balance', othersEach: '0.020', othersTotal: '0.100' },
];

export default function AluminumBilletEn() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.ALUMINUM_BILLET_CASTING_PLANT_1_302}
            alt="Aluminum Billet and Ingot"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Link
              to="/en"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <h1 className="text-5xl font-bold text-white mb-6">
              Aluminum Billet / Ingot
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Primary aluminum and primary alloyed aluminum products — extrusion billets,
              rolled flat billets, and casting ingots for all your industrial needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Aluminum Billet Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Header */}
            {/* Full-width image at top */}
            <div className="relative rounded-2xl overflow-hidden mb-10">
              <img
                src={IMAGES.ALUMINYUM_BILLET_NEDIR}
                alt="Aluminum Billet"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute bottom-4 left-4">
                <span className="bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">Primary Aluminum</span>
              </div>
            </div>
            <div className="mb-12">
              <div>
                <h2 className="text-3xl font-bold text-primary mb-2">Aluminum Billet / Ingot</h2>
                <p className="text-lg text-muted-foreground font-medium mb-4">Primary Aluminum</p>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  With our extensive supplier network and strong support services, we supply high-purity
                  primary aluminum billets to all our customers. Various alloy options are available for
                  extrusion, rolling, and casting applications.
                </p>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary" /> Supply Forms
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">15-24 kg ingots</span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">750 kg T-Bar</span>
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">700 kg pig iron shapes</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Table 1: Standard Purity (P series) */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Standard Purity — Chemical Composition (Max %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-24 kg ingots, 750 kg T-Bar and 700 kg pig iron shapes</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alloy (Max)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">V %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ga %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                      <th className="text-center px-3 py-2 font-semibold">Others Each</th>
                      <th className="text-center px-3 py-2 font-semibold">Others Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardPurityTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.v}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ga}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table 2: High Purity (P series) */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> High Purity — Chemical Composition (Max %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-22 kg ingots and 600 kg T-Bar</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alloy (Max)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                      <th className="text-center px-3 py-2 font-semibold">Na %</th>
                      <th className="text-center px-3 py-2 font-semibold">V %</th>
                      <th className="text-center px-3 py-2 font-semibold">B %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ga %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                      <th className="text-center px-3 py-2 font-semibold">Others Each</th>
                      <th className="text-center px-3 py-2 font-semibold">Others Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {highPurityTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.na}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.v}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.b}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ga}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table 3: A-Series */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> A-Series — Chemical Composition (Max %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-24 kg ingots and 750 kg T-Bar</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alloy (Max)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Sn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aSeriesTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.sn}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.ga}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Table 4: EC Grade */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> EC Grade — Chemical Composition (Max %)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">* 15-24 kg ingots, 750 kg T-Bar and 700 kg pig iron shapes</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alloy (Max)</th>
                      <th className="text-center px-3 py-2 font-semibold">Si %</th>
                      <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                      <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                      <th className="text-center px-3 py-2 font-semibold">V %</th>
                      <th className="text-center px-3 py-2 font-semibold">B %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ga %</th>
                      <th className="text-center px-3 py-2 font-semibold">Pb %</th>
                      <th className="text-center px-3 py-2 font-semibold">Ni %</th>
                      <th className="text-center px-3 py-2 font-semibold">Al (min)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ecGradeTable.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.v}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.b}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ga}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.pb}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.ni}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Primary Alloyed Aluminium Section */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Section Header */}
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-primary mb-4">Primary Alloyed Aluminium</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                We supply extrusion billets, rolled flat billets (slabs), and casting ingots/T-bars
                to our customers with primary alloyed aluminum product requirements across different industries.
              </p>
            </div>

            {/* Extrusion Billets */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-primary mb-2">Primary Aluminium Extrusion Billets</h3>
              <p className="text-muted-foreground mb-2">Alloyed billet (1xxx–7xxx) series with diameters from 4" (100 mm) to 17" (430 mm) and lengths of 5800 mm, 6000 mm and 7000 mm.</p>
              <p className="text-sm text-muted-foreground mb-6">We can also supply parts to customer specifications in 2xxx, 3xxx, 6xxx, 5xxx and 7xxx alloys.</p>
              <div className="border border-border rounded-xl overflow-hidden mb-6">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" /> Chemical Composition (Max %)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alloy (Max)</th>
                        <th className="text-center px-3 py-2 font-semibold">Si %</th>
                        <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                        <th className="text-center px-3 py-2 font-semibold">Zn max %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                        <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                        <th className="text-center px-3 py-2 font-semibold">Others Each</th>
                        <th className="text-center px-3 py-2 font-semibold">Others Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alloyedBilletTable.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                          <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Mechanical Properties */}
              <div className="border border-border rounded-xl overflow-hidden mb-4">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" /> Mechanical Properties
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Property</th>
                        <th className="text-center px-4 py-2 font-semibold">T5</th>
                        <th className="text-center px-4 py-2 font-semibold">T6</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-background">
                        <td className="px-4 py-2 font-medium text-primary">Tensile strength (MPa)</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">160</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">190</td>
                      </tr>
                      <tr className="bg-muted/20">
                        <td className="px-4 py-2 font-medium text-primary">Yield strength (MPa)</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">120</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">150</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Metallurgical & Physical Quality */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-5">
                  <h4 className="font-semibold text-primary mb-3">Metallurgical Quality</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Average grain size: min 2000 grains/cm² (max 200 per ASTM E112-96)</li>
                    <li>• Inverse segregation zone: &lt;200 µ</li>
                    <li>• Inclusions filtered through ceramic foam filter (min 30 ppi)</li>
                    <li>• Max inclusion count: 20-50 µ: &lt;7 | 51-100 µ: &lt;1 | &gt;100 µ: 0</li>
                  </ul>
                </div>
                <div className="bg-card border border-border rounded-xl p-5">
                  <h4 className="font-semibold text-primary mb-3">Physical Quality</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Max surface damage: 2 mm depth, 5 mm width, 250 mm length</li>
                    <li>• Tolerances: up to 203 mm: +0/-2 mm; 228 mm and above: +0/-3 mm</li>
                    <li>• Marking: one end cut per stack, with cast number and alloy code</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Rolled Flat Billets (Slabs) */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-primary mb-2">Primary Aluminium Rolled Flat Billets (Slabs)</h3>
              <p className="text-muted-foreground mb-6">1xxx, 3xxx, 5xxx and 6xxx series rolled flat billets (slabs) supplied to customer specifications. Custom dimensions available on request.</p>
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" /> Slab Dimensions
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-center px-4 py-2 font-semibold">Thickness (mm)</th>
                        <th className="text-center px-4 py-2 font-semibold">Width (mm)</th>
                        <th className="text-center px-4 py-2 font-semibold">Length (mm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slabTable.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                          <td className="px-4 py-2 text-center font-medium text-primary">{row.thickness}</td>
                          <td className="px-4 py-2 text-center text-muted-foreground">{row.width}</td>
                          <td className="px-4 py-2 text-center text-muted-foreground">{row.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Casting Ingots / T-Bars */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-primary mb-2">Primary Alloyed Casting Ingots / T-Bars</h3>
              <p className="text-muted-foreground mb-2">Compliant with EN1706, EN AC42100. Certifications: ISO/TS9001, ISO/TS16949, ISO14001, ISO18000, ISO27000.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">Open mold ingot: 7-15 kg</span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">Continuous mold ingot: 6-8 kg</span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">Continuous cast bar: 10 kg</span>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">T-bar weight range: 300-750 kg</span>
              </div>
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="bg-primary/5 px-6 py-3 border-b border-border">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Award className="w-4 h-4 text-primary" /> Chemical Composition (Max %)
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Alloy</th>
                        <th className="text-center px-3 py-2 font-semibold">Si %</th>
                        <th className="text-center px-3 py-2 font-semibold">Fe %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Mg %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cu %</th>
                        <th className="text-center px-3 py-2 font-semibold">Ti %</th>
                        <th className="text-center px-3 py-2 font-semibold">Zn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Cr %</th>
                        <th className="text-center px-3 py-2 font-semibold">Sn %</th>
                        <th className="text-center px-3 py-2 font-semibold">Sr %</th>
                        <th className="text-center px-3 py-2 font-semibold">AL</th>
                        <th className="text-center px-3 py-2 font-semibold">Others Each</th>
                        <th className="text-center px-3 py-2 font-semibold">Others Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {castingIngotsTable.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                          <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.si}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.fe}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.mg}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cu}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.ti}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.zn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.cr}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.sn}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.sr}</td>
                          <td className="px-3 py-2 text-center font-semibold text-primary">{row.al}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersEach}</td>
                          <td className="px-3 py-2 text-center text-muted-foreground">{row.othersTotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Aluminium Wire Rod Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-primary mb-4">Aluminium Wire Rod</h2>
              <p className="text-lg text-muted-foreground max-w-3xl">
                We supply high-quality aluminium wire rod in various alloy grades for electrical and industrial applications.
                Available in standard coil formats with precise chemical and mechanical property guarantees.
              </p>
            </div>

            {/* Chemical Composition Table 1: Pure Alloys */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Chemical Composition — Pure Alloys (Max %)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Alloy</th>
                      <th className="text-center px-4 py-2 font-semibold">Al content (min %)</th>
                      <th className="text-center px-4 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-4 py-2 font-semibold">Si %</th>
                      <th className="text-center px-4 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-4 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-4 py-2 font-semibold">Ti+V+Mn+Cr %</th>
                      <th className="text-center px-4 py-2 font-semibold">Other each %</th>
                      <th className="text-center px-4 py-2 font-semibold">Other total %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { alloy: '1080', al: '99.8', fe: '0.12', si: '0.10', cu: '0.01', zn: '0.04', tiVMnCr: '0.01', otherEach: '0.02', otherTotal: '0.20' },
                      { alloy: '1370', al: '99.7', fe: '0.25', si: '0.10', cu: '0.02', zn: '0.04', tiVMnCr: '0.04', otherEach: '0.02', otherTotal: '0.10' },
                      { alloy: '1350', al: '99.5', fe: '0.40', si: '0.10', cu: '0.05', zn: '0.05', tiVMnCr: '0.04', otherEach: '0.03', otherTotal: '0.10' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-4 py-2 font-medium text-primary">{row.alloy}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.al}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.fe}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.si}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.cu}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.zn}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.tiVMnCr}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.otherEach}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{row.otherTotal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Chemical Composition Table 2: Alloy 6101 */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> Chemical Composition — Alloy 6101 (Max %)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Alloy</th>
                      <th className="text-center px-4 py-2 font-semibold">Al content (min %)</th>
                      <th className="text-center px-4 py-2 font-semibold">Fe %</th>
                      <th className="text-center px-4 py-2 font-semibold">Si %</th>
                      <th className="text-center px-4 py-2 font-semibold">Cu %</th>
                      <th className="text-center px-4 py-2 font-semibold">Zn %</th>
                      <th className="text-center px-4 py-2 font-semibold">Mg %</th>
                      <th className="text-center px-4 py-2 font-semibold">Mn %</th>
                      <th className="text-center px-4 py-2 font-semibold">Cr %</th>
                      <th className="text-center px-4 py-2 font-semibold">B %</th>
                      <th className="text-center px-4 py-2 font-semibold">Other each %</th>
                      <th className="text-center px-4 py-2 font-semibold">Other total %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-background">
                      <td className="px-4 py-2 font-medium text-primary">6101</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">remain</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.50</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.7</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.10</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.10</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.80</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.03</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.03</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.06</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.03</td>
                      <td className="px-4 py-2 text-center text-muted-foreground">0.10</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Physical & Mechanical Properties Table */}
            <div className="mb-10 border border-border rounded-xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-3 border-b border-border">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" /> Physical &amp; Mechanical Properties
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Alloy</th>
                      <th className="text-center px-3 py-2 font-semibold">Diameter (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Coil Weight (kg)</th>
                      <th className="text-center px-3 py-2 font-semibold">Coil Height (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Int. Diameter (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Ext. Diameter (mm)</th>
                      <th className="text-center px-3 py-2 font-semibold">Tensile Strength (MPa)</th>
                      <th className="text-center px-3 py-2 font-semibold">Elongation (% min)</th>
                      <th className="text-center px-3 py-2 font-semibold">Resistivity (ohm.mm²/m)</th>
                      <th className="text-center px-3 py-2 font-semibold">Conductivity % IACS min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { alloy: '1080 H11', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '80–95', elong: '15', resist: '0.02772', conduct: '62.2' },
                      { alloy: '1350/1370 H11', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '80–95', elong: '15', resist: '0.02785', conduct: '61.9' },
                      { alloy: '1350/1370 H12', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '95–110', elong: '15', resist: '0.02794', conduct: '61.7' },
                      { alloy: '1350/1370 H13', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '550', extDia: '1420', tensile: '105–120', elong: '15', resist: '0.02794', conduct: '61.7' },
                      { alloy: '1350/1370 H14', dia: '9.5–12', weight: '1700–2000', height: '850', intDia: '500', extDia: '1420', tensile: '115–130', elong: '15', resist: '0.0280', conduct: '61.6' },
                      { alloy: '6101 T4', dia: '9.5', weight: '2000 ± 200', height: '850–950', intDia: '500', extDia: '1500', tensile: '150 min', elong: '10', resist: '0.03260', conduct: '52.9' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                        <td className="px-3 py-2 font-medium text-primary whitespace-nowrap">{row.alloy}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.dia}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.weight}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.height}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.intDia}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.extDia}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.tensile}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.elong}</td>
                        <td className="px-3 py-2 text-center text-muted-foreground">{row.resist}</td>
                        <td className="px-3 py-2 text-center font-semibold text-primary">{row.conduct}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-primary mb-6">Would You Like a Quote?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Our expert team will contact you for your aluminum billet and ingot requirements.
            </p>
            <Button asChild size="lg" className="px-8">
              <Link to="/en/contact#quote-form">Request Quote</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}